/* Copyright (c) 2024 MOCA Systems, Inc. All rights reserved. */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CameraTicketAlignment, FocusOnTicketsOptions } from '@appCanvasCommon/interfaces/camera.interface';
import { Ticket } from '@touchplan/common';
import {
	BehaviorSubject,
	combineLatest,
	debounceTime,
	distinctUntilChanged,
	filter,
	map,
	Observable,
	Subject,
	takeUntil,
	withLatestFrom,
} from 'rxjs';
import { ITicketAttachmentList } from 'src/app/app-data/models/Attachment';
import { CustomFieldListType } from 'src/app/app-data/state/CustomFieldsApi';
import { MultiPanelItemDirective } from 'src/app/components/multi-panel/multi-panel-item/multi-panel-item.directive';

import { MultiPanelComponent } from 'src/app/components/multi-panel/multi-panel.component/multi-panel.component';
import { ImportTicketsCommonService } from 'src/app/modules/import-tickets/services/import-tickets-common.service';
import { FilteredTicketListPanelComponent } from 'src/app/modules/plan-tickets-filter/components/filtered-ticket-list-panel/filtered-ticket-list-panel.component';
import { PlanTicketsFilterComponent } from 'src/app/modules/plan-tickets-filter/components/plan-tickets-filter/plan-tickets-filter.component';
import { ITicketCustomFields } from 'src/app/modules/plan-tickets-filter/interfaces/plan-tickets-filter.interface';
import { Interaction } from 'src/app/plain-javascript/services/interaction-blocker';
import { lazyLoadWhen } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { AppState } from 'src/app/services/app-state.service';
import { PlanState } from 'src/app/services/plan-state.service';
import { PlanTicketsFilterRemoteControlService } from 'src/app/services/plan-tickets-filter-remote-control.service';
import { TicketEditCommonDesktopService } from 'src/app/common/docked-ticket/services/ticket-edit-common-desktop.service';
import { PlanPrintPanelComponent } from '../plan-print-panel/plan-print-panel.component';
import { PlanPrintPanelRemoteControlService } from 'src/app/services/plan-print-panel-remote-control.service';

const MODULES = [CommonModule];

const DIRECTIVES = [
	FilteredTicketListPanelComponent,
	PlanTicketsFilterComponent,
	PlanPrintPanelComponent,
	MultiPanelComponent,
	MultiPanelItemDirective,
];

/**
 * Intended for use only on the plan page, [[PlanState]] is injected
 */
@Component({
	standalone: true,
	selector: 'tp-plan-filter-panel',
	templateUrl: './plan-filter-panel.component.html',
	styleUrls: ['./plan-filter-panel.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [...MODULES, ...DIRECTIVES],
	providers: [],
})
export class PlanFilterPanelComponent implements OnInit, OnDestroy {
	private route = inject(ActivatedRoute);
	private importTicketsCommonService = inject(ImportTicketsCommonService);
	private ticketEditCommonDesktopService = inject(TicketEditCommonDesktopService);

	/**
	 * Injecting the planState. This is a wrapper component, and would require significant
	 * connection boilerplate on the plan.component
	 * The children: filter panel, the ticket list ui, and multi panel are all intended to be reusable
	 */
	public planState = inject(PlanState);
	public appState = inject(AppState);
	public planTicketsFilterRemoteControlService = inject(PlanTicketsFilterRemoteControlService);
	public planPrintPanelRemoteControlService = inject(PlanPrintPanelRemoteControlService);

	private projectId: string | null = null;
	private planId: string | null = null;

	protected ticketListForFiltering$: Observable<Map<string, Ticket>>;
	protected ticketCustomFieldMapping$: Observable<ITicketCustomFields>;

	private _matchingTickets$ = new BehaviorSubject<Map<string, Ticket>>(new Map());
	private _nonMatchingTickets$ = new BehaviorSubject<Map<string, Ticket>>(new Map());
	private _nonMatchingTicketsTransparencyOverwrite$ = new BehaviorSubject<number | null>(null);

	private shutdownSubject = new Subject<void>();

	protected ticketAttachmentsForFiltering$: Observable<ITicketAttachmentList>;

	public matchingTickets$ = this._matchingTickets$.asObservable();

	public ngOnInit(): void {
		this.projectId = this.route.snapshot.paramMap.get('projectId');
		this.planId = this.route.snapshot.paramMap.get('planId');

		this.ticketListForFiltering$ = this.planState.tickets.list$.pipe(debounceTime(1000));
		this.ticketCustomFieldMapping$ = this.getTicketCustomFieldDefinitions$();

		this.listenFocusTicketChange();
		this.renderTicketListOnTicketFilterChanges();
		this.applyTransparencyChanges();

		this.ticketAttachmentsForFiltering$ = lazyLoadWhen(
			() => this.planState.fileAttachCloneService.getTicketIdsWithAttachments$(this.projectId, this.planId),
			this.planTicketsFilterRemoteControlService.openPlanTicketsFilter$.pipe(filter((open) => open)),
			this.shutdownSubject
		);
	}

	public ngOnDestroy(): void {
		this._matchingTickets$.complete();
		this._nonMatchingTickets$.complete();
		this._nonMatchingTicketsTransparencyOverwrite$.complete();
		this.shutdownSubject.next();
		this.shutdownSubject.complete();
	}

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6420
	// TODO: Move to its own service
	private getTicketCustomFieldDefinitions$(): Observable<ITicketCustomFields> {
		return this.planState.customFieldsApi
			.getCustomFieldTicketValues(CustomFieldListType.TicketValue)
			.data.list$.pipe(
				map((ticketValues) => {
					const result: ITicketCustomFields = {};
					ticketValues.forEach((value, _) => {
						Object.keys(value.view).forEach((customFieldDefKey) => {
							result[value.$id] = [
								...(result[value.$id] || []),
								{
									customFieldId: customFieldDefKey,
									value: value.view[customFieldDefKey].value,
								},
							];
						});
					});

					return result;
				}),
				filter((data) => !!Object.keys(data).length)
			);
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-6420

	public onMatchingTicketsChange(matchingTickets: Map<string, Ticket>) {
		this._matchingTickets$.next(matchingTickets);
	}

	// Handles the event from the filtered-ticket-list-panel component
	// Calls the focusOnTicket with custom options
	public onFocusTicketFromFilterTicketList(ticket: Ticket) {
		this.focusOnTicket(ticket, { align: CameraTicketAlignment.LEFT, z: 0.5 });
	}

	public onTransparencyChange(value: number) {
		this._nonMatchingTicketsTransparencyOverwrite$.next(value);
	}

	// Finds the distinct tickets that are in `sourceTickets` but not in `baselineTickets`.
	private getDistinctTickets(
		sourceTickets: Map<string, Ticket>,
		baselineTickets: Map<string, Ticket>
	): Map<string, Ticket> {
		const distinctTickets = new Map<string, Ticket>();

		sourceTickets.forEach((ticket) => {
			// Check if the current ticket is not present in the baseline map
			if (!baselineTickets.has(ticket.$id)) {
				distinctTickets.set(ticket.$id, ticket);
			}
		});

		return distinctTickets;
	}

	/**
	 * Checks if the tickets in `sourceTickets` are identical to those in `baselineTickets`.
	 * Returns `true` if all tickets in `sourceTickets` are found in `baselineTickets` with the same IDs.
	 * Returns `false` if at least one ticket from `sourceTickets` is not found in `baselineTickets` or if the sizes differ.
	 */
	private areTicketsIdentical(
		sourceTickets: Map<string, Ticket>,
		baselineTickets: Map<string, Ticket>
	): boolean {
		if (sourceTickets.size !== baselineTickets.size) {
			return false;
		}
		for (const ticket of sourceTickets.values()) {
			if (!baselineTickets.has(ticket.$id)) {
				return false;
			}
		}
		return true;
	}

	private applyTransparencyChanges(): void {
		this._nonMatchingTicketsTransparencyOverwrite$
			.pipe(withLatestFrom(this._nonMatchingTickets$), takeUntil(this.shutdownSubject))
			.subscribe(([transparency, nonMatchingTickets]) => {
				nonMatchingTickets.forEach((ticket) => {
					this.planState.filterTransparencyValue$.next(transparency);
					this.planState.actions.updateTicketLiveData(ticket.$id, { overwriteTransparency: transparency });
				});
			});
	}

	// Focuses a ticket in the canvas and updates selection/cycle state if needed
	private focusOnTicket(ticket: Ticket, focusOnTicketsOptions?: FocusOnTicketsOptions) {
		const selectedCanvasTickets = this.planState.tickets.selectedTicket$.getValue().list;
		const hasMultipleCanvasSelectedTickets = selectedCanvasTickets.size > 1;
		const selectedFloatingTickets = this.planState.floatingTickets.selectedTicket$.getValue().list;
		// Determine the correct edited ticket list (canvas or floating)
		const editTicketList = this.planState.floatingTickets.editTicket$.getValue().size
			? this.planState.floatingTickets
			: this.planState.tickets;
		const currentEditTicket = editTicketList.editTicket$.getValue();
		const hasOpenEditTicket = currentEditTicket?.size > 0;

		// If there's an open edit ticket, ensure it can be closed before switching
		if (hasOpenEditTicket) {
			const canCloseCurrentEditTicket =
				this.planState.interactionBlocker.check(Interaction.OpenEditTicket) ||
				this.planState.interactionBlocker.check(Interaction.SwitchEditTicket);

			if (!canCloseCurrentEditTicket) {
				return;
			}

			// Persist and reset the view of the currently edited ticket
			this.ticketEditCommonDesktopService.trySave();
			this.planState.actions.changeTicketViewToDefault(currentEditTicket, editTicketList);
		}

		// Update cycleTicket only if there are multiple selected tickets
		if (hasMultipleCanvasSelectedTickets) {
			this.updateCycleTicket(ticket, selectedCanvasTickets);
		} else if (selectedCanvasTickets.size === 1) {
			// unselect if there is only one ticket selected
			this.planState.actions.unSelectTicket(selectedCanvasTickets, this.planState.tickets);
		} else if (selectedFloatingTickets.size) {
			// If there are selected floating tickets, unselect them
			this.planState.actions.unSelectTicket(selectedFloatingTickets, this.planState.floatingTickets);
		}

		// Focus on the new ticket
		this.planState.actions.focusTicket(ticket, { focusOnTicketsOptions });
	}

	// Updates the cycleTicket state if the clicked ticket is not in the selected list
	private updateCycleTicket(ticket: Ticket, selectedTickets: Map<string, Ticket>) {
		const isInList = this.checkSelectedTicketsContainTicket(selectedTickets, ticket.$id);
		this.planState.actions.unselectCycleTicket(selectedTickets, this.planState.tickets);
		if (!isInList) {
			// Ticket not in list → clear ticket selection
			this.planState.actions.unSelectTicket(selectedTickets, this.planState.tickets);
			// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7252
			// Currently, the cycle ticket code doesn't work very well, so it's not possible to reset it to null
			// so we set it to an empty object
			this.planState.cycleTicket$.next({} as Ticket);
			// END PLACEHOLDER REPLACE AS PART OF TP-7252
			return;
		}
		// Keep multiple tickets and set the new cycle ticket
		this.planState.actions.selectCycleTicket(ticket);
		this.planState.cycleTicket$.next(ticket);
	}

	// Utility: check if a ticket is in the selected list
	private checkSelectedTicketsContainTicket(tickets: Map<string, Ticket>, id: string): boolean {
		return tickets.has(id);
	}

	// Handles the event of a ticket being selected from the import tickets process
	// Calls the focusOnTicket with custom options
	private listenFocusTicketChange(): void {
		this.importTicketsCommonService.focusTicket$
			.pipe(
				filter((focusTicket) => !!focusTicket),
				takeUntil(this.shutdownSubject)
			)
			.subscribe((ticket) => {
				this.focusOnTicket(ticket, { z: 0.5 });
			});
	}

	private renderTicketListOnTicketFilterChanges(): void {
		combineLatest([
			this._matchingTickets$.pipe(distinctUntilChanged(this.areTicketsIdentical)),
			this.planTicketsFilterRemoteControlService.openPlanTicketsFilter$,
		])
			.pipe(
				withLatestFrom(
					this._nonMatchingTickets$,
					this.planState.tickets.list$,
					this._nonMatchingTicketsTransparencyOverwrite$
				),
				takeUntil(this.shutdownSubject)
			)
			.subscribe(([[matchingTickets, isFilterOpen], storedNonMatchingTickets, planTickets, transparency]) => {
				if (!isFilterOpen) {
					this._nonMatchingTickets$.next(new Map());
					this.planState.filterTransparencyValue$.next(null);
					planTickets.forEach((ticket) => {
						this.planState.actions.updateTicketLiveData(ticket.$id, { overwriteTransparency: null });
					});
					return;
				}
				const nonMatchingTickets = this.getDistinctTickets(planTickets, matchingTickets);
				this._nonMatchingTickets$.next(nonMatchingTickets);
				if (nonMatchingTickets.size === 0) {
					this.planState.filterTransparencyValue$.next(null);
				} else {
					this.planState.filterTransparencyValue$.next(transparency);
				}
				// In order to apply changes to only tickets that changed their matching status to improve performance,
				// we need to find the difference between the stored non-matching tickets and the current non-matching tickets
				const addNonMatchingTickets = this.getDistinctTickets(nonMatchingTickets, storedNonMatchingTickets);
				addNonMatchingTickets.forEach((ticket) => {
					// Unselect tickets that no longer match the current filter.
					this.planState.actions.unSelectTicket(ticket);
					this.planState.actions.unselectCycleTicket(ticket);

					// Update the ticket's live data, ensuring this action is called last
					// to prevent earlier actions from altering the transparency state.
					this.planState.actions.updateTicketLiveData(ticket.$id, {
						overwriteTransparency: transparency,
					});
				});
				const removeNonMatchingTickets = this.getDistinctTickets(
					storedNonMatchingTickets,
					nonMatchingTickets
				);
				removeNonMatchingTickets.forEach((ticket) => {
					this.planState.actions.updateTicketLiveData(ticket.$id, { overwriteTransparency: null });
				});
			});
	}
}
